import {
	AppShell,
	Avatar,
	Button,
	Group,
	Menu,
	NavLink,
	Text,
	Title,
	UnstyledButton,
} from "@mantine/core";
import {
	IconBuilding,
	IconChevronDown,
	IconDashboard,
	IconHistory,
	IconKey,
	IconLicense,
	IconTags,
	IconLogout,
	IconSettings,
	IconUsers,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { navigationService } from "../../services/navigationService";
import { logout } from "../../store/auth/authSlice";
import type { AppDispatch, RootState } from "../../store/store";
import { toggleSidebar } from "../../store/ui/uiSlice";

interface LayoutProps {
	children: ReactNode;
}

function Layout({ children }: LayoutProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch<AppDispatch>();

	const { user } = useSelector((state: RootState) => state.auth);
	const { sidebarOpened } = useSelector((state: RootState) => state.ui);

	const handleLogout = () => {
		navigationService.clearLastRoute();
		dispatch(logout());
		navigate("/login");
	};

	const navigationItems = [
		{
			icon: IconDashboard,
			label: "Dashboard",
			path: "/dashboard",
			roles: ["admin", "manager", "viewer"],
		},
		{
			icon: IconBuilding,
			label: "Accounts",
			path: "/accounts",
			roles: ["admin", "manager", "viewer"],
		},
		{
			icon: IconLicense,
			label: "Licenses",
			path: "/licenses",
			roles: ["admin", "manager", "viewer"],
		},
		{
			icon: IconTags,
			label: "License Types",
			path: "/license-types",
			roles: ["admin", "manager"],
		},
		{
			icon: IconUsers,
			label: "Users",
			path: "/users",
			roles: ["admin", "manager"],
		},
		{
			icon: IconHistory,
			label: "Activity",
			path: "/activity",
			roles: ["admin", "manager"],
		},
		{
			icon: IconKey,
			label: "API Keys",
			path: "/api-keys",
			roles: ["admin"],
		},
	];

	const filteredNavItems = navigationItems.filter(
		(item) => user && item.roles.includes(user.role)
	);

	return (
		<AppShell
			navbar={{
				width: 300,
				breakpoint: "sm",
				collapsed: { mobile: !sidebarOpened },
			}}
			header={{ height: 60 }}
			padding="md">
			<AppShell.Header>
				<Group
					h="100%"
					px="md"
					justify="space-between">
					<Group>
						<Button
							variant="subtle"
							onClick={() => dispatch(toggleSidebar())}
							hiddenFrom="sm">
							☰
						</Button>
						<Title order={3}>License Management</Title>
					</Group>

					<Menu
						shadow="md"
						width={200}>
						<Menu.Target>
							<UnstyledButton>
								<Group gap="xs">
									<Avatar
										size="sm"
										radius="xl">
										{user?.username?.charAt(0).toUpperCase()}
									</Avatar>
									<div style={{ flex: 1 }}>
										<Text
											size="sm"
											fw={500}>
											{user?.username}
										</Text>
										<Text
											size="xs"
											c="dimmed">
											{user?.role}
										</Text>
									</div>
									<IconChevronDown size={14} />
								</Group>
							</UnstyledButton>
						</Menu.Target>

						<Menu.Dropdown>
							<Menu.Item leftSection={<IconSettings size={14} />}>
								Settings
							</Menu.Item>
							<Menu.Divider />
							<Menu.Item
								leftSection={<IconLogout size={14} />}
								color="red"
								onClick={handleLogout}>
								Logout
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				</Group>
			</AppShell.Header>

			<AppShell.Navbar p="md">
				<AppShell.Section grow>
					{filteredNavItems.map((item) => (
						<NavLink
							key={item.path}
							href={item.path}
							label={item.label}
							leftSection={<item.icon size="1rem" />}
							active={location.pathname === item.path}
							onClick={(e) => {
								e.preventDefault();
								// Use navigation service to handle route change and persistence
								navigationService.navigateTo(item.path);
							}}
							mb="xs"
						/>
					))}
				</AppShell.Section>
			</AppShell.Navbar>

			<AppShell.Main>{children}</AppShell.Main>
		</AppShell>
	);
}

export default Layout;
